import React, { useState, useEffect, useRef } from "react";
import searchIcon from "../assets/icon/general/mapPin.png";

// URL base per le ricerche su Nominatim
const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";

const Search = ({
  placeholder = "Cerca un luogo",
  onSearch,
  className = "",
  defaultValue = "",
}) => {
  const [searchTerm, setSearchTerm] = useState(defaultValue);
  const [suggestions, setSuggestions] = useState([]);           // lista di suggerimenti
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Aggiorna searchTerm quando cambia defaultValue
  useEffect(() => {
    if (defaultValue) {
      setSearchTerm(defaultValue);
    }
  }, [defaultValue]);

  // Effettua la chiamata a Nominatim con debounce
  useEffect(() => {
    if (searchTerm.trim().length < 3) {
      // Se la stringa è troppo corta, resettiamo i suggerimenti
      setSuggestions([]);
      setIsDropdownVisible(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      const url = `${NOMINATIM_URL}` +
                  `?q=${encodeURIComponent(searchTerm)}` +
                  `&countrycodes=it` +
                  `&format=json` +
                  `&addressdetails=1` +
                  `&limit=5` +
                  `&accept-language=it`;

      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(`Nominatim ha risposto con status ${res.status}`);
          return res.json();
        })
        .then((data) => {
          // Filtriamo solo i risultati di tipo "via"/"street"/"road"/"pedestrian" ecc.
          const roads = data.filter((item) => {
            const type = item.type;
            return (
              type === "road" ||
              type === "residential" ||
              type === "pedestrian" ||
              type === "highway" ||
              type === "street"
            );
          });

          // Mappiamo in un array di suggerimenti utili
          const formatted = roads.map((item) => ({
            displayName: item.display_name,
            lat: item.lat,
            lon: item.lon,
          }));

          setSuggestions(formatted);
          setIsDropdownVisible(formatted.length > 0);
        })
        .catch((err) => {
          console.error("Errore nel fetch di Nominatim:", err);
          setSuggestions([]);
          setIsDropdownVisible(false);
        });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Nasconde la dropdown quando si clicca all'esterno
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setIsDropdownVisible(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (onSearch) onSearch(searchTerm);
      setIsDropdownVisible(false);
    }
  };

  const handleSearchClick = () => {
    if (onSearch) onSearch(searchTerm);
    setIsDropdownVisible(false);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.displayName);
    setIsDropdownVisible(false);
    if (onSearch) {
      onSearch(suggestion.displayName, {
        lat: suggestion.lat,
        lon: suggestion.lon,
      });
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center">
        <img
          src={searchIcon}
          alt="Search"
          className="absolute left-3 w-4 opacity-60"
        />
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="border bg-primary border-secondary pl-10 pr-3 py-2 rounded-full w-full text-smokeWhite 
                     focus:outline-none focus:ring-2 focus:ring-secondary"
          onFocus={() => {
            if (suggestions.length > 0) setIsDropdownVisible(true);
          }}
        />
        <button onClick={handleSearchClick} className="hidden"></button>
      </div>

      {/* Dropdown dei suggerimenti allargata e con sfondo nero e testo bianco */}
      {isDropdownVisible && suggestions.length > 0 && (
        <ul
          ref={dropdownRef}
          className="absolute z-10 mt-1 w-full bg-black border border-gray-700 rounded-lg shadow-lg max-h-80 overflow-auto"
        >
          {suggestions.map((item, idx) => (
            <li
              key={idx}
              onClick={() => handleSuggestionClick(item)}
              className="px-4 py-3 hover:bg-gray-700 cursor-pointer text-white text-base"
            >
              {item.displayName}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Search;
